import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";

const ResourceDetailPage = () => {
  const { t, isRtl } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const handleBack = useBackNavigation("/ressources");

  const resource = t.resources.items.find((r) => r.slug === slug);
  if (!resource) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="container max-w-3xl">
          <Link to="/ressources" onClick={handleBack} className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <BackArrow className="w-4 h-4" />
            {t.common.backHome}
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              {resource.title}
            </h1>
            <p className="text-lg text-muted-foreground font-body mb-10">
              {resource.description}
            </p>
          </motion.div>

          <motion.div
            className="bg-card rounded-2xl border border-border shadow-card p-8 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ul className="space-y-4">
              {resource.content.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground font-body leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {resource.slug === 'candidature' && (
            <motion.div
              className="grid sm:grid-cols-2 gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Button asChild className="h-auto p-4 flex-col items-center justify-center gap-2 border-border shadow-sm bg-primary text-white hover:bg-primary/90 transition-all hover:scale-105">
                <Link to="/candidat-info#general">
                  <span className="font-display font-bold text-lg">Parcours des candidats</span>
                  <span className="font-body text-xs text-white/80 text-center">Conditions - Financement - Processus</span>
                </Link>
              </Button>
              <Button asChild className="h-auto p-4 flex-col items-center justify-center gap-2 border-border shadow-sm bg-accent text-accent-foreground hover:bg-accent/90 transition-all hover:scale-105">
                <Link to="/candidat-info#jeunes">
                  <span className="font-display font-bold text-lg">Jeunes -35 ans</span>
                  <span className="font-body text-xs text-accent-foreground/80 text-center whitespace-nowrap overflow-hidden text-ellipsis w-full">Nouveau : Décret n° 2.26.311 - Soutien financier aux candidats de moins de 35 ans</span>
                </Link>
              </Button>
            </motion.div>
          )}

          <motion.div
            className="bg-card rounded-2xl border border-border shadow-card p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-display font-bold text-foreground mb-6">
              🔗 Liens utiles
            </h2>
            <div className="space-y-3">
              {resource.links.map((link, i) => {
                const isInternal = link.url.startsWith('/');
                return isInternal ? (
                  <Link
                    key={i}
                    to={link.url}
                    className="flex items-center justify-between p-4 rounded-lg border border-border bg-background hover:shadow-card hover:border-primary/30 transition-all group"
                  >
                    <span className="font-body font-medium text-foreground group-hover:text-primary transition-colors">
                      {link.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                ) : (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 rounded-lg border border-border bg-background hover:shadow-card hover:border-primary/30 transition-all group"
                  >
                    <span className="font-body font-medium text-foreground group-hover:text-primary transition-colors">
                      {link.label}
                    </span>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </a>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResourceDetailPage;
